import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Save, ChevronDown, Clock, Check, Loader2,
  FileText, ArrowLeft, Download, Upload, Copy,
  MoreHorizontal, Sparkles
} from 'lucide-react';

interface HeaderProps {
  templateName: string;
  isDirty: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  onSave: () => void;
  onNameChange: (name: string) => void;
}

export function Header({
  templateName,
  isDirty,
  isSaving,
  lastSaved,
  onSave,
  onNameChange,
}: HeaderProps) {
  const navigate = useNavigate();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(templateName);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameSubmit = () => {
    if (editedName.trim()) {
      onNameChange(editedName.trim());
    } else {
      setEditedName(templateName);
    }
    setIsEditingName(false);
  };

  const formatLastSaved = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <header className="flex items-center gap-4 px-4 py-3 border-b border-primary-700/50 bg-primary-900/50 backdrop-blur-sm">
      {/* Back button */}
      <Link
        to="/templates"
        className="flex items-center gap-2 text-primary-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
      </Link>
      
      {/* Logo */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-primary-900" />
        </div>
      </div>
      
      {/* Divider */}
      <div className="w-px h-6 bg-primary-700" />
      
      {/* Template name */}
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-primary-400" />
        {isEditingName ? (
          <input
            ref={nameInputRef}
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNameSubmit();
              if (e.key === 'Escape') {
                setEditedName(templateName);
                setIsEditingName(false);
              }
            }}
            className="px-2 py-1 bg-primary-800 border border-accent-500/50 rounded text-white font-medium focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        ) : (
          <button
            onClick={() => {
              setEditedName(templateName);
              setIsEditingName(true);
            }}
            className="px-2 py-1 text-white font-medium hover:bg-white/5 rounded transition-colors"
          >
            {templateName}
          </button>
        )}
        
        {/* Dirty indicator */}
        {isDirty && (
          <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
        )}
      </div>
      
      {/* Spacer */}
      <div className="flex-1" />
      
      {/* Last saved */}
      {lastSaved && !isDirty && (
        <div className="flex items-center gap-2 text-sm text-primary-400">
          <Clock className="w-4 h-4" />
          <span>Saved {formatLastSaved(lastSaved)}</span>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Export */}
        <button className="btn-ghost" title="Export">
          <Download className="w-4 h-4" />
        </button>
        
        {/* Import */}
        <button className="btn-ghost" title="Import">
          <Upload className="w-4 h-4" />
        </button>
        
        {/* More */}
        <button className="btn-ghost" title="More options">
          <MoreHorizontal className="w-4 h-4" />
        </button>
        
        {/* Save button */}
        <motion.button
          onClick={onSave}
          disabled={isSaving || !isDirty}
          whileTap={{ scale: 0.95 }}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all
            ${isDirty
              ? 'bg-accent-500 text-primary-900 hover:bg-accent-400'
              : 'bg-primary-700 text-primary-400 cursor-not-allowed'
            }
          `}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save
            </>
          )}
        </motion.button>
      </div>
    </header>
  );
}
